import { z } from 'zod'
import { calendarDaySchema, instantSchema } from '../validators/temporal.validator.js'
import { prismImportJobStatusSchema } from '../enums/prism-import-job-status.enum.js'

/** Which slice of a Social Tables listing an import walks. */
const PRISM_IMPORT_WHENS = ['upcoming', 'past', 'all'] as const
export const prismImportWhenSchema = z.enum(PRISM_IMPORT_WHENS)
export type PrismImportWhen = z.infer<typeof prismImportWhenSchema>

/** Options used when a Prism import job was started. */
export const prismImportJobConfigSchema = z.object({
  /** Import these Social Tables event UUIDs (takes precedence over list mode). */
  eventIds: z.array(z.string().min(1)).optional(),
  /** When listing from ST: how many events to take from the page. */
  limit: z.number().int().positive().max(50).optional(),
  /**
   * Which page of that listing, 1-based. The page size is `limit`, so page 2 with
   * limit 50 is events 51–100. Social Tables sorts by date ascending, so paging
   * walks forward in time from the soonest upcoming event.
   */
  page: z.number().int().positive().optional(),
  /**
   * Walk every page of the listing instead of one. `limit` still caps the page
   * size (Social Tables allows 50), so this is how you import a list longer than
   * one page without running the job once per page.
   */
  allPages: z.boolean().optional(),
  /**
   * Import events that have no floor plan in Social Tables or Prismm. On by
   * default so dates, clients and documents are not dropped — Find missing would
   * keep rediscovering them. They may file under Unassigned when Prism gives no
   * venue (an event's venue cannot be changed later). Untick to skip them.
   */
  includeWithoutFloorplan: z.boolean().optional(),
  /**
   * Import events whose Social Tables owner or Prismm venue matches this name
   * (case-insensitive, either name may contain the other). Walks the whole
   * listing for `when`. Pasted `eventIds` still win if both are set.
   */
  venueName: z
    .string()
    .trim()
    .min(3, 'Venue name needs at least 3 characters.')
    .max(200)
    .optional(),
  when: prismImportWhenSchema.optional(),
  dryRun: z.boolean().optional(),
  forceNewEvent: z.boolean().optional(),
  /**
   * Leave CSE events whose names already match a Social Tables / Prismm event.
   * The default import updates those in place; this is how a venue re-run only
   * picks up what is new.
   */
  skipExisting: z.boolean().optional(),
  /**
   * Leave Social Tables / Prismm events whose date is before today, even when
   * the listing is "all". An everything-run should not recrawl last year's floor
   * plans unless someone asks for past.
   */
  skipPast: z.boolean().optional(),
  skipCreateVenue: z.boolean().optional(),
  /**
   * Replace plans imported before CSE kept track of what an import wrote, even
   * where they no longer match the source. Those are normally left alone,
   * because from here an edit made in CSE and a change in Prismm look the same.
   * This is for the one-off re-run that moves plans drawn in an old frame into
   * the right one — and it overwrites any edits people made to those plans in
   * CSE. Plans changed since a tracked import, and plans open right now, are
   * still left alone.
   */
  replaceUntrackedPlans: z.boolean().optional(),
  /**
   * The CSE event this job replaces: "Re-import from scratch" on the catalog.
   *
   * The old event is deleted only after the job has brought the new one in with its
   * furniture, and kept as it was otherwise. It used to be deleted before the job
   * even existed, so an import that then failed left nothing at all.
   *
   * Set by that route alone. `startPrismImportInputSchema` leaves it out, so starting
   * an import cannot name some other event to be deleted.
   */
  replacesEventId: z.string().uuid().optional(),
})

export type PrismImportJobConfig = z.infer<typeof prismImportJobConfigSchema>

export const prismImportJobResultSchema = z.object({
  eventsProcessed: z.number().int().nonnegative(),
  eventsSucceeded: z.number().int().nonnegative(),
  eventsFailed: z.number().int().nonnegative(),
  venuesCreated: z.number().int().nonnegative().optional(),
  /**
   * Events imported with no floor plan, because Social Tables had no diagram for
   * them yet. They carry their dates, clients and documents; someone still has to
   * draw the plan. Worth a number of its own — it is most of a migration.
   */
  eventsWithoutFloorplan: z.number().int().nonnegative().optional(),
  clientsAdded: z.number().int().nonnegative().optional(),
  documentsAdded: z.number().int().nonnegative().optional(),
  /** Events left alone because CSE already had one with the same name. */
  eventsSkipped: z.number().int().nonnegative().optional(),
  /** Events left alone because their date is before today. */
  eventsSkippedPast: z.number().int().nonnegative().optional(),
  /**
   * Events left for a later run because Prismm did not answer.
   *
   * Separate from both "failed" and "without a floor plan" on purpose: nothing is
   * wrong with the event, and importing it as a plan-less shell would be worse
   * than leaving it — the shell's name is what skip-existing matches on, so the
   * next run would pass over the very events whose plans were missed.
   */
  eventsDeferred: z.number().int().nonnegative().optional(),
  /**
   * Events Social Tables listed floor plans for, that came in with no furniture on
   * any of them — nothing the import could read and place.
   *
   * Not "succeeded". These used to be logged as imported like any other, and they
   * were most of the empty plans at Milagro: the event existed with its dates and
   * clients, so it looked done, and a venue manager later found the plan blank.
   */
  eventsWithEmptyPlans: z.number().int().nonnegative().optional(),
  dryRun: z.boolean().optional(),
})

export type PrismImportJobResult = z.infer<typeof prismImportJobResultSchema>

export const prismImportJobDtoSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  createdById: z.string().uuid(),
  createdByName: z.string(),
  status: prismImportJobStatusSchema,
  config: prismImportJobConfigSchema,
  log: z.string(),
  result: prismImportJobResultSchema.nullable(),
  error: z.string().nullable(),
  startedAt: instantSchema.nullable(),
  finishedAt: instantSchema.nullable(),
  createdAt: instantSchema,
  updatedAt: instantSchema,
})

export type PrismImportJobDto = z.infer<typeof prismImportJobDtoSchema>

export const prismImportJobListDtoSchema = z.object({
  jobs: z.array(prismImportJobDtoSchema),
  total: z.number().int().nonnegative(),
})

export type PrismImportJobListDto = z.infer<typeof prismImportJobListDtoSchema>

/**
 * The stored Social Tables session, described but never disclosed.
 *
 * The session itself is a live sign-in for the importer's account, so the API
 * reports only what an admin needs in order to decide whether to replace it: that
 * one is stored, when it was last updated and by whom, when its cookies start to
 * expire, and whether a run has used it. `envFallback` says a
 * `PRISM_SESSION_STATE` variable is also set, which is what the importer falls
 * back to when no session is stored.
 */
export const prismSessionDtoSchema = z.object({
  present: z.boolean(),
  cookieCount: z.number().int().nonnegative().nullable(),
  expiresAt: instantSchema.nullable(),
  teamId: z.number().int().nullable(),
  lastUsedAt: instantSchema.nullable(),
  updatedAt: instantSchema.nullable(),
  updatedByName: z.string().nullable(),
  envFallback: z.boolean(),
})

export type PrismSessionDto = z.infer<typeof prismSessionDtoSchema>

/**
 * One Social Tables event in the tenant's kept catalog, and where CSE stands on it.
 *
 * `stEventUid` is Social Tables' own id — the value the importer takes as an event
 * link, and what a scan matches on so a rescan appends rather than starts over.
 * `eventId` is the CSE event when there is one, which is what makes `imported`
 * true; `verified` is a person's word that the imported plan is accurate, and no
 * import can set it.
 */
export const prismCatalogEventDtoSchema = z.object({
  id: z.string().uuid(),
  stEventUid: z.string().min(1),
  name: z.string(),
  /**
   * The calendar day Social Tables lists, as UTC `yyyy-MM-dd`.
   *
   * A date-only listing is midnight UTC. Sending that instant and formatting it
   * in a western zone shows the day before — the same off-by-one the importer
   * already avoids when it files the event. This is the day, not a wall clock.
   */
  startsAt: calendarDaySchema.nullable(),
  /**
   * Social Tables' "owner". A venue for a venue-owned event and a person for a
   * planner-owned one, so it is never shown as the venue — see `venueName`.
   */
  ownerName: z.string().nullable(),
  /**
   * The venue this event is at, once it has been imported: the only venue for a
   * listing row that can be trusted to be a place.
   */
  venueName: z.string().nullable(),
  prismmOccasionId: z.number().int().positive().nullable(),
  /** The CSE event imported from this listing row, if it is in CSE. */
  eventId: z.string().uuid().nullable(),
  imported: z.boolean(),
  /**
   * Imported, but with no floor plan to show for it.
   *
   * An event imports its dates, clients and documents whether or not a plan came
   * with it — Prismm may not have answered, or the occasion may genuinely have
   * none — and a listing that only said "imported" gave no way to tell the two
   * apart. False for a row that has not been imported at all.
   */
  importedWithoutFloorPlan: z.boolean(),
  /**
   * When an import passed this event over, and why, in words meant to be read.
   *
   * Set instead of filing the event under a holding venue: an event whose venue
   * the source will not name has nowhere to go, and inventing a place for it made
   * a bucket of events nobody could act on. Cleared the moment it imports, so a
   * reason here always describes why the event is absent now.
   */
  skippedAt: instantSchema.nullable(),
  skippedReason: z.string().nullable(),
  verified: z.boolean(),
  verifiedAt: instantSchema.nullable(),
  verifiedByName: z.string().nullable(),
  firstSeenAt: instantSchema,
  lastSeenAt: instantSchema,
})

export type PrismCatalogEventDto = z.infer<typeof prismCatalogEventDtoSchema>

/** The whole kept catalog, with the counts the page puts in its summary line. */
export const prismCatalogDtoSchema = z.object({
  /** One page of the catalog, filtered as asked. */
  events: z.array(prismCatalogEventDtoSchema),
  /**
   * The whole catalog, not this page and not this filter.
   *
   * Unlike every other list here, where `total` is what the query matched: this
   * one sits beside `importedCount` and `verifiedCount` in a summary line that
   * is about the catalog ("340 events · 120 imported · 90 verified"), and a
   * number that changed when somebody ticked a filter would be describing
   * something else. `matched` is the one a pager reads.
   */
  total: z.number().int().nonnegative(),
  importedCount: z.number().int().nonnegative(),
  verifiedCount: z.number().int().nonnegative(),
  /** How many rows the filter matched, across every page. */
  matched: z.number().int().nonnegative(),
  /** When a scan last refreshed the catalog, null before the first one. */
  lastScanAt: instantSchema.nullable(),
})

export type PrismCatalogDto = z.infer<typeof prismCatalogDtoSchema>

/** What a scan added to the catalog, alongside the refreshed list. */
export const prismCatalogScanDtoSchema = z.object({
  listed: z.number().int().nonnegative(),
  added: z.number().int().nonnegative(),
  updated: z.number().int().nonnegative(),
  catalog: prismCatalogDtoSchema,
})

export type PrismCatalogScanDto = z.infer<typeof prismCatalogScanDtoSchema>

/**
 * How much tenant data a wipe covers — read before as a warning, returned after
 * as a receipt.
 *
 * Venues, tracks and staff are deliberately absent: the point is to clear what an
 * import creates so it can be run again from a known state, not to take the
 * organization apart. Client contacts are counted because an import creates them,
 * and furniture covers the catalog, the arrangements built from it and the stock
 * counts that reference it.
 */
export const prismWipeCountsSchema = z.object({
  events: z.number().int().nonnegative(),
  venueSpaces: z.number().int().nonnegative(),
  inventoryVersions: z.number().int().nonnegative(),
  clientUsers: z.number().int().nonnegative(),
  furnitureItems: z.number().int().nonnegative(),
  furnitureGroups: z.number().int().nonnegative(),
})

export type PrismWipeCounts = z.infer<typeof prismWipeCountsSchema>

/** What a wipe would remove, plus what it will not touch, for the confirmation. */
export const prismWipePreviewDtoSchema = z.object({
  counts: prismWipeCountsSchema,
  /** Venues are kept — named so the confirmation can say what survives. */
  venuesKept: z.number().int().nonnegative(),
  /** The environment this would run in ("staging", "production", …). */
  environment: z.string(),
})

export type PrismWipePreviewDto = z.infer<typeof prismWipePreviewDtoSchema>

/** What a wipe actually removed. */
export const prismWipeResultDtoSchema = z.object({ counts: prismWipeCountsSchema })

export type PrismWipeResultDto = z.infer<typeof prismWipeResultDtoSchema>
